import React, { useState } from 'react'
import { Box, Typography } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'
import { track } from '@amplitude/analytics-browser'
import { CoreTextField, CoreButton } from 'src/components/core'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import { createTagAction } from 'src/reducers/tag/action'
import { TAG_COLORS, DEFAULT_TAG_COLOR } from 'src/model/tag-colors'

const VISIBILITY_OPTIONS = [
  {
    value: false,
    label: 'Private',
    icon: 'mdi:lock-outline',
    description: 'Only you and your team can see this tag.',
  },
  {
    value: true,
    label: 'Public',
    icon: 'mdi:web',
    description: 'Shown to learners on catalog.',
  },
]

const CreateTagDialog = ({ onCreated }) => {
  const dispatch = useDispatch()
  const [name, setName] = useState('')
  const [color, setColor] = useState(DEFAULT_TAG_COLOR)
  const [isPublic, setIsPublic] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const trimmed = name.trim()
  const disabled = !trimmed || submitting

  const handleCreate = () => {
    if (disabled) return
    setSubmitting(true)
    dispatch(
      createTagAction(
        { name: trimmed, color, isPublic },
        tag => {
          setSubmitting(false)
          dispatch(messageBoxHideAction())
          if (tag) {
            track('tag_created', {
              tag_id: tag.id,
              tag_name: tag.name,
              tag_color: tag.color,
              tag_visibility: tag.isPublic ? 'public' : 'private',
            })
          }
          if (onCreated && tag) onCreated(tag)
        },
        () => setSubmitting(false),
      ),
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <CoreTextField
        fullWidth
        label="Tag Name"
        placeholder="Enter tag name..."
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyPress={e => {
          if (e.key === 'Enter') handleCreate()
        }}
      />

      <Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: '8px' }}>
          Tag Color
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          {TAG_COLORS.map(c => (
            <Box
              key={c}
              role="button"
              onClick={() => setColor(c)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                backgroundColor: c,
                cursor: 'pointer',
                outline:
                  c === color ? '2px solid #3D7CCE' : '2px solid transparent',
                outlineOffset: '2px',
              }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: '8px' }}>
          Visibility
        </Typography>
        <Box sx={{ display: 'flex', gap: '12px' }}>
          {VISIBILITY_OPTIONS.map(option => {
            const selected = option.value === isPublic
            return (
              <Box
                key={option.label}
                role="button"
                onClick={() => setIsPublic(option.value)}
                sx={{
                  flex: 1,
                  p: '12px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: selected ? '1px solid #3D7CCE' : '1px solid #e0e4ea',
                  backgroundColor: selected ? '#f4f8fd' : 'transparent',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    mb: '4px',
                  }}
                >
                  <Icon icon={option.icon} width={14} height={14} />
                  <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {option.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                  {option.description}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          mt: '4px',
        }}
      >
        <CoreButton
          onClick={() => dispatch(messageBoxHideAction())}
          variant="text"
          color="secondary"
        >
          Cancel
        </CoreButton>
        <CoreButton onClick={handleCreate} color="primary" disabled={disabled}>
          Create
        </CoreButton>
      </Box>
    </Box>
  )
}

export default CreateTagDialog
